import React from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaPhone, FaEnvelope, FaListUl } from 'react-icons/fa';
import { CONTACT, phoneHref } from '../constants/contact';
import './PolicyPage.css';

const sections = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    content: (
      <>
        <p>Opulence Technologies, operating <strong>MaxAutoParts</strong> at <strong>maxautoparts.online</strong>, collects information you provide directly to us when you use our website, place orders, or communicate with our team. The types of information we collect include:</p>
        <h3>Personal Identification Information</h3>
        <ul>
          <li>Full name, email address, phone number, and mailing/billing address</li>
          <li>Vehicle information including year, make, model, and VIN number</li>
          <li>Payment card details (processed securely via PCI-compliant payment processors — we never store full card numbers)</li>
          <li>Order history and part search queries</li>
        </ul>
        <h3>Automatically Collected Information</h3>
        <ul>
          <li>IP address, browser type, operating system, and referring URLs</li>
          <li>Pages visited, time spent on site, and click-through patterns</li>
          <li>Cookie data and similar tracking technologies for session management and analytics</li>
          <li>Device identifiers for mobile users</li>
        </ul>
        <h3>Information from Third Parties</h3>
        <ul>
          <li>Shipping carrier tracking data (FedEx, UPS, USPS)</li>
          <li>Payment processor confirmation data</li>
          <li>Marketing platform data if you engage with our ads or email campaigns</li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use-information',
    title: 'How We Use Your Information',
    content: (
      <>
        <p>Opulence Technologies uses the information we collect to operate, maintain, and improve MaxAutoParts services. Specifically, we use your data to:</p>
        <ul>
          <li>Process and fulfill your auto parts orders, including verifying fitment and dispatching shipments</li>
          <li>Communicate with you about order status, shipping updates, and delivery confirmations</li>
          <li>Provide customer support and respond to inquiries submitted via our website or phone</li>
          <li>Send transactional emails such as order confirmations, invoices, and warranty information</li>
          <li>Improve our website, product catalog, and user experience through analytics</li>
          <li>Send promotional offers, newsletters, and product updates (only with your consent)</li>
          <li>Detect, investigate, and prevent fraudulent activity, abuse, or unauthorized access</li>
          <li>Comply with legal obligations including tax reporting and consumer protection laws</li>
          <li>Personalize your browsing experience by remembering your vehicle preferences</li>
        </ul>
        <p>We will never sell, rent, or trade your personal information to third parties for their own marketing purposes without your explicit consent.</p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies & Tracking Technologies',
    content: (
      <>
        <p>MaxAutoParts uses cookies and similar technologies to enhance your browsing experience and understand how visitors use our site. Types of cookies we use include:</p>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for the website to function properly (shopping cart, login sessions, security tokens)</li>
          <li><strong>Analytics Cookies:</strong> Help us understand traffic patterns and improve site performance (e.g., Google Analytics)</li>
          <li><strong>Preference Cookies:</strong> Remember your vehicle selections, region settings, and display preferences</li>
          <li><strong>Marketing Cookies:</strong> Used to serve relevant ads and measure campaign effectiveness</li>
        </ul>
        <p>You may disable cookies through your browser settings. Note that disabling essential cookies may affect the functionality of our website, including the ability to add parts to your cart or complete checkout.</p>
      </>
    ),
  },
  {
    id: 'sharing-information',
    title: 'Sharing of Information',
    content: (
      <>
        <p>We may share your information with select third parties only as necessary to provide our services:</p>
        <ul>
          <li><strong>Shipping Partners:</strong> FedEx, UPS, and USPS receive your name, address, and order details to deliver your parts</li>
          <li><strong>Payment Processors:</strong> Stripe and other PCI-compliant processors handle payment transactions securely</li>
          <li><strong>Parts Suppliers:</strong> Our verified supplier network may receive vehicle details to fulfil custom orders</li>
          <li><strong>Analytics Services:</strong> Anonymized usage data may be shared with Google Analytics and similar platforms</li>
          <li><strong>Legal Authorities:</strong> We will disclose information if required by law, subpoena, or to protect our rights</li>
        </ul>
        <p>All third-party partners are contractually required to protect your data and may only use it for the specific purpose for which it was shared.</p>
      </>
    ),
  },
  {
    id: 'data-security',
    title: 'Data Security',
    content: (
      <>
        <p>Opulence Technologies takes the security of your personal data seriously. We implement industry-standard measures to protect your information from unauthorized access, alteration, disclosure, or destruction:</p>
        <ul>
          <li>SSL/TLS encryption for all data transmitted between your browser and our servers</li>
          <li>PCI-DSS compliant payment processing — we never store raw credit card data on our servers</li>
          <li>Regular security audits and vulnerability assessments</li>
          <li>Access controls ensuring only authorized personnel can access customer data</li>
          <li>Encrypted database storage for sensitive personal information</li>
        </ul>
        <p>While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. In the event of a data breach, we will notify affected users in accordance with applicable law.</p>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your Rights & Choices',
    content: (
      <>
        <p>You have the following rights regarding your personal information held by Opulence Technologies / MaxAutoParts:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
          <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time via the unsubscribe link or by contacting us</li>
          <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
          <li><strong>Restriction:</strong> Request that we limit how we process your data in certain circumstances</li>
        </ul>
        <p>To exercise any of these rights, please contact our Privacy Team at <a href="mailto:privacy@maxautoparts.online">privacy@maxautoparts.online</a> or visit our Contact page. We will respond within 30 days.</p>
      </>
    ),
  },
  {
    id: 'third-party-links',
    title: 'Third-Party Links',
    content: (
      <>
        <p>Our website may contain links to third-party websites, including payment processors, shipping carriers, and social media platforms. These external sites have their own privacy policies and we are not responsible for their content or practices. We encourage you to review the privacy policy of any third-party site you visit.</p>
      </>
    ),
  },
  {
    id: 'childrens-privacy',
    title: "Children's Privacy",
    content: (
      <>
        <p>MaxAutoParts is not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If you believe a child has submitted personal information to us, please contact us immediately at <a href="mailto:privacy@maxautoparts.online">privacy@maxautoparts.online</a> and we will promptly delete such information.</p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    content: (
      <>
        <p>Opulence Technologies reserves the right to update this Privacy Policy at any time. We will notify users of significant changes by posting a prominent notice on <strong>maxautoparts.online</strong> and updating the "Last Updated" date below. Your continued use of our website after any changes constitutes acceptance of the updated policy.</p>
        <p>We recommend reviewing this policy periodically to stay informed about how we protect your information.</p>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="policy-page">
      <div className="policy-hero">
        <div className="policy-hero-badge">
          <FaShieldAlt /> Legal
        </div>
        <h1>Privacy Policy</h1>
        <div className="policy-hero-meta">
          <span>Opulence Technologies</span>
          <span>|</span>
          <span>maxautoparts.online</span>
          <span>|</span>
          <span>Last Updated: May 15, 2026</span>
        </div>
      </div>

      <div className="policy-body">
        <div className="policy-toc">
          <h3><FaListUl /> Table of Contents</h3>
          <ol>
            {sections.map((s, i) => (
              <li key={i}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </div>

        {sections.map((s, i) => (
          <div className="policy-section" id={s.id} key={i}>
            <h2>
              <span className="section-num">{i + 1}</span>
              {s.title}
            </h2>
            {s.content}
          </div>
        ))}

        <div className="policy-contact-box">
          <h3>Questions About This Policy?</h3>
          <p>Our Privacy Team at Opulence Technologies is here to help. Reach out any time.</p>
          <div className="policy-contact-links">
            <a href="mailto:privacy@maxautoparts.online" className="link-primary">
              <FaEnvelope /> privacy@maxautoparts.online
            </a>
            <a href={phoneHref} className="link-secondary">
              <FaPhone /> {CONTACT.phone}
            </a>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: '#a0aec0' }}>
          &copy; 2026 Opulence Technologies. All rights reserved. |{' '}
          <Link to="/terms-and-conditions" style={{ color: '#FFA500' }}>Terms and Conditions</Link> |{' '}
          <Link to="/refund-policy" style={{ color: '#FFA500' }}>Refund Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
